import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Spinner from '../components/Spinner';

const BASE_URL = '/api';
const getToken = () => localStorage.getItem('ozon_token');

async function fetchCancel(period) {
  const r = await fetch(`${BASE_URL}/analytics/cancel?period=${period}`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

// Ozon iptal sebebi ID → Türkçe bilgi (veritabanından doğrulandı)
const REASON_META = {
  // ── Alıcı kaynaklı — kontrol edemezsin ───────────────────────────────
  // "Покупатель отменил заказ" — genel alıcı iptali
  502: { tr: 'Alıcı siparişi iptal etti',      category: 'alici', eylem: null },
  503: { tr: 'Alıcı siparişi iptal etti',      category: 'alici', eylem: null },
  504: { tr: 'Alıcı siparişi iptal etti',      category: 'alici', eylem: null },
  508: { tr: 'Alıcı siparişi iptal etti',      category: 'alici', eylem: null },
  710: { tr: 'Alıcı siparişi iptal etti',      category: 'alici', eylem: null },
  // "не устроил срок доставки" — teslimat süresi
  505: { tr: 'Teslimat süresi uygun değil',    category: 'alici', eylem: 'Teslimat süresini kısaltmayı dene' },
  // "нашел дешевле" — daha ucuz buldu
  506: { tr: 'Daha ucuzunu buldu',             category: 'alici', eylem: 'Fiyat rekabetini gözden geçir' },
  // "Покупатель не забрал заказ" — teslim almadı
  665: { tr: 'Alıcı paketi teslim almadı',     category: 'alici', eylem: null },
  // "товар не подошел" — teslimatta red, alıcı kaynaklı
   79: { tr: 'Teslimatta red: ürün uymadı',   category: 'alici', eylem: null },
  578: { tr: 'Teslimatta red: ürün uymadı',   category: 'alici', eylem: null },
  647: { tr: 'Teslimatta red: ürün uymadı',   category: 'alici', eylem: null },

  // ── Satıcı etkili — önlem alınabilir ─────────────────────────────────
  // "недоволен качеством" — kalite sorunu, puanı etkiler
  587: { tr: 'Teslimatta red: kalite sorunu',  category: 'satici_etkili', eylem: 'Paketleme ve ürün kalite kontrolünü gözden geçir' },
  // "неполная комплектация" — eksik içerik, puanı etkiler
   20: { tr: 'Teslimatta red: eksik içerik',  category: 'satici_etkili', eylem: 'Paketleme sürecini kontrol et, içerik listesini doğrula' },

  // ── Satıcı iptali — puanı etkiler ────────────────────────────────────
  // "Товар закончился на складе" — stok tükendi, puanı etkiler
  352: { tr: 'Stok tükendi (depoda ürün yok)', category: 'satici', eylem: 'Stok takibini sıkılaştır, düşük stokta ürünü pasife al' },
  // "Вы отменили заказ" — satıcı manuel iptal, puanı etkiler
  402: { tr: 'Satıcı tarafından iptal edildi', category: 'satici', eylem: 'İptal sebebini incele; stok veya süreç sorunu varsa çöz' },
};

// Kategori tanımları
const CATEGORIES = [
  {
    key: 'satici_etkili',
    label: 'Satıcı Etkili',
    desc: 'Önlem alabilirsin — puanı etkiler',
    icon: '🔴',
    border: 'border-red-200',
    bg: 'bg-red-50',
    badge: 'bg-red-100 text-red-700',
    headerBg: 'bg-red-50/60',
  },
  {
    key: 'satici',
    label: 'Satıcı İptali',
    desc: 'Senin açtığın iptaller — puanı etkiler',
    icon: '🟠',
    border: 'border-orange-200',
    bg: 'bg-orange-50',
    badge: 'bg-orange-100 text-orange-700',
    headerBg: 'bg-orange-50/60',
  },
  {
    key: 'alici',
    label: 'Alıcı Kaynaklı',
    desc: 'Doğrudan kontrolün dışında',
    icon: '🟡',
    border: 'border-yellow-200',
    bg: 'bg-yellow-50',
    badge: 'bg-yellow-100 text-yellow-700',
    headerBg: 'bg-yellow-50/60',
  },
];

function getMeta(id, ru) {
  if (REASON_META[id]) return REASON_META[id];
  // Rusça metin fallback (bilinmeyen ID'ler için)
  if (!ru) return { tr: `Sebep #${id}`, category: 'alici', eylem: null };
  if (ru.includes('закончился на складе'))  return { tr: 'Stok tükendi (depoda ürün yok)', category: 'satici', eylem: 'Stok takibini sıkılaştır' };
  if (ru.includes('нашел дешевле'))         return { tr: 'Daha ucuzunu buldu', category: 'alici', eylem: 'Fiyat rekabetini gözden geçir' };
  if (ru.includes('срок доставки'))         return { tr: 'Teslimat süresi uygun değil', category: 'alici', eylem: 'Teslimat süresini kısaltmayı dene' };
  if (ru.includes('качеством'))             return { tr: 'Teslimatta red: kalite sorunu', category: 'satici_etkili', eylem: 'Paketleme kalitesini gözden geçir' };
  if (ru.includes('комплектац'))            return { tr: 'Teslimatta red: eksik içerik', category: 'satici_etkili', eylem: 'Paketleme sürecini kontrol et' };
  if (ru.includes('вручении'))              return { tr: 'Teslimatta red: ürün uymadı', category: 'alici', eylem: null };
  if (ru.includes('не забрал'))             return { tr: 'Alıcı paketi teslim almadı', category: 'alici', eylem: null };
  if (ru.includes('Вы отменили'))           return { tr: 'Satıcı tarafından iptal edildi', category: 'satici', eylem: 'İptal sebebini incele' };
  if (ru.includes('Покупатель отменил'))    return { tr: 'Alıcı siparişi iptal etti', category: 'alici', eylem: null };
  return { tr: ru, category: 'alici', eylem: null };
}

const PERIODS = [
  { label: '30 Gün',  value: 30  },
  { label: '90 Gün',  value: 90  },
  { label: '180 Gün', value: 180 },
];

function fmtNum(n) { return Number(n || 0).toLocaleString('tr-TR'); }
function fmtDt(s) {
  if (!s) return '—';
  return new Date(s).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function ReasonRow({ r, period, catStyle }) {
  const [open,    setOpen]    = useState(false);
  const [orders,  setOrders]  = useState(null);
  const [loading, setLoading] = useState(false);
  const meta = getMeta(r.reason_id, r.reason_ru);

  const loadOrders = useCallback(async () => {
    if (orders !== null) return;
    setLoading(true);
    try {
      const ids = (r.reason_ids || [r.reason_id]).join(',');
      const res = await fetch(
        `${BASE_URL}/analytics/cancel/orders?reason_ids=${ids}&period=${period}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setOrders(await res.json());
    } catch { setOrders([]); }
    finally { setLoading(false); }
  }, [r.reason_ids, r.reason_id, period, orders]);

  function toggle() {
    setOpen(o => !o);
    if (!open) loadOrders();
  }

  return (
    <div className={`rounded-xl border overflow-hidden ${r.puan_etkiler ? 'border-red-300' : 'border-gray-100'}`}>
      <button onClick={toggle}
        className={`w-full flex items-center gap-3 p-3 text-left transition-colors ${
          r.puan_etkiler ? 'bg-red-50/40 hover:bg-red-50' : 'bg-white hover:bg-gray-50'
        }`}>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-gray-800">{meta.tr}</div>
          {meta.tr.startsWith('Sebep #') && r.reason_ru && (
            <div className="text-xs text-gray-400 italic mt-0.5">{r.reason_ru}</div>
          )}
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {r.after_ship && (
              <span className="text-[11px] bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded">
                Sevkiyat sonrası
              </span>
            )}
            {r.puan_etkiler && (
              <span className="text-[11px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-semibold">
                ⚠️ Puanı etkiler
              </span>
            )}
            {meta.eylem && (
              <span className="text-[11px] bg-blue-50 text-blue-500 px-1.5 py-0.5 rounded">
                💡 {meta.eylem}
              </span>
            )}
          </div>
        </div>
        <span className="flex-shrink-0 text-lg font-bold text-gray-700 mr-1">{r.adet}</span>
        <span className="flex-shrink-0 text-gray-400 text-sm">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="border-t border-gray-100 bg-gray-50/50">
          {loading && <div className="flex justify-center py-4"><Spinner size="sm" /></div>}
          {!loading && orders && orders.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">Sipariş bulunamadı</p>
          )}
          {!loading && orders && orders.length > 0 && (
            <div className="divide-y divide-gray-100">
              {orders.map(o => (
                <div key={o.posting_number} className="px-4 py-2.5 flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <Link to={`/orders/${o.posting_number}`}
                      className="font-mono text-xs text-blue-600 hover:underline font-semibold">
                      {o.posting_number}
                    </Link>
                    <div className="mt-0.5 space-y-0.5">
                      {(o.items || []).map((item, i) => (
                        <div key={i} className="text-xs text-gray-600">
                          <span className="font-medium text-gray-800">{item.quantity}×</span> {item.offer_id}
                          <span className="text-gray-400 ml-1">{item.name?.slice(0, 40)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <span className="text-[11px] text-gray-400 flex-shrink-0 whitespace-nowrap">
                    {fmtDt(o.in_process_at)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CategorySection({ cat, reasons, period, totalAll }) {
  const [collapsed, setCollapsed] = useState(false);
  const catReasons = reasons.filter(r => getMeta(r.reason_id, r.reason_ru).category === cat.key);
  if (catReasons.length === 0) return null;

  const catTotal = catReasons.reduce((s, r) => s + r.adet, 0);
  const pct = totalAll > 0 ? ((catTotal / totalAll) * 100).toFixed(0) : 0;

  return (
    <div className={`border ${cat.border} rounded-2xl overflow-hidden`}>
      <button
        onClick={() => setCollapsed(c => !c)}
        className={`w-full flex items-center gap-3 px-5 py-4 ${cat.headerBg} text-left`}>
        <span className="text-lg">{cat.icon}</span>
        <div className="flex-1">
          <div className="font-semibold text-gray-900 text-sm">{cat.label}</div>
          <div className="text-xs text-gray-500">{cat.desc}</div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-bold px-2 py-1 rounded-lg ${cat.badge}`}>
            {catTotal} iptal
          </span>
          <span className="text-xs text-gray-400 w-10 text-right">%{pct}</span>
          <span className="text-gray-400 text-sm ml-1">{collapsed ? '▼' : '▲'}</span>
        </div>
      </button>

      {!collapsed && (
        <div className="p-4 space-y-2 bg-white">
          {catReasons.map((r, i) => (
            <ReasonRow key={`${r.reason_id}-${i}`} r={r} period={period} catStyle={cat} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function CancelAnalysis() {
  const [period,  setPeriod]  = useState(90);
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  useEffect(() => {
    setLoading(true); setError('');
    fetchCancel(period)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [period]);

  const totalAll = data?.summary?.toplam || 0;

  return (
    <div className="p-3 sm:p-6 max-w-5xl mx-auto space-y-6">

      {/* Başlık + Period */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">İptal Analizi</h1>
          <p className="text-sm text-gray-400 mt-0.5">Sebep bazlı iptal kategorileri ve eylem önerileri</p>
        </div>
        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
          {PERIODS.map(p => (
            <button key={p.value} onClick={() => setPeriod(p.value)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                period === p.value ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="flex justify-center py-16"><Spinner size="lg" /></div>}
      {error   && <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">{error}</div>}

      {!loading && !error && data && (
        <>
          {/* ── Özet Kartlar ─────────────────────────────────────────────── */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {[
              { label: 'Toplam İptal',       value: fmtNum(data.summary.toplam),            bg: 'bg-gray-50',    text: 'text-gray-800' },
              { label: 'Alıcı Kaynaklı',     value: fmtNum(data.summary.alici),             bg: 'bg-blue-50',    text: 'text-blue-700' },
              { label: 'Satıcı İptali',      value: fmtNum(data.summary.satici),            bg: 'bg-orange-50',  text: 'text-orange-700' },
              { label: '⚠️ Puan Etkileyen',  value: fmtNum(data.summary.puan_etkileyen),   bg: 'bg-red-50',     text: 'text-red-700' },
              { label: 'Sevkiyat Sonrası',   value: fmtNum(data.summary.sevkiyat_sonrasi), bg: 'bg-purple-50',  text: 'text-purple-700' },
            ].map(c => (
              <div key={c.label} className={`${c.bg} rounded-xl p-4 text-center`}>
                <div className={`text-2xl font-bold ${c.text}`}>{c.value}</div>
                <div className="text-xs text-gray-500 mt-1">{c.label}</div>
              </div>
            ))}
          </div>

          {/* ── Kategorilere Göre İptal Sebepleri ───────────────────────── */}
          <div>
            <h2 className="font-semibold text-gray-900 mb-1">İptal Sebepleri — Kategoriye Göre</h2>
            <p className="text-xs text-gray-400 mb-4">Son {period} gün · Her kategoriye tıklayarak sebepler ve siparişler görüntülenir</p>

            {data.reasons.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-10">Bu dönemde iptal yok</p>
            ) : (
              <div className="space-y-3">
                {CATEGORIES.map(cat => (
                  <CategorySection
                    key={cat.key}
                    cat={cat}
                    reasons={data.reasons}
                    period={period}
                    totalAll={totalAll}
                  />
                ))}
              </div>
            )}
          </div>

          {/* ── Ürün Bazlı İptal ─────────────────────────────────────────── */}
          {data.byProduct.length > 0 && (
            <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm">
              <h2 className="font-semibold text-gray-900 mb-0.5">Ürün Bazlı İptal Oranı</h2>
              <p className="text-xs text-gray-400 mb-4">Son {period} gün — iptal oranına göre sıralı</p>

              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[480px]">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left py-2 text-xs font-semibold text-gray-400 uppercase">#</th>
                      <th className="text-left py-2 text-xs font-semibold text-gray-400 uppercase">Ürün</th>
                      <th className="text-right py-2 text-xs font-semibold text-gray-400 uppercase w-16">Toplam</th>
                      <th className="text-right py-2 text-xs font-semibold text-gray-400 uppercase w-16">Satılan</th>
                      <th className="text-right py-2 text-xs font-semibold text-gray-400 uppercase w-16">İptal</th>
                      <th className="text-right py-2 text-xs font-semibold text-gray-400 uppercase w-20">Oran</th>
                      <th className="py-2 w-32"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {data.byProduct.map((p, i) => {
                      const rate = parseFloat(p.iptal_orani) || 0;
                      const barColor = rate >= 20 ? '#ef4444' : rate >= 10 ? '#f97316' : '#eab308';
                      return (
                        <tr key={p.offer_id} className="hover:bg-gray-50/60">
                          <td className="py-2.5 text-xs text-gray-300 pr-2">{i + 1}</td>
                          <td className="py-2.5 pr-4">
                            <div className="font-semibold text-xs text-gray-800">{p.offer_id}</div>
                            <div className="text-[11px] text-gray-400 truncate max-w-[180px]">{p.name}</div>
                          </td>
                          <td className="py-2.5 text-right text-sm text-gray-400">{fmtNum(p.toplam)}</td>
                          <td className="py-2.5 text-right text-sm text-gray-600">{fmtNum(p.satilan)}</td>
                          <td className="py-2.5 text-right text-sm font-semibold text-red-500">{fmtNum(p.iptal)}</td>
                          <td className="py-2.5 text-right">
                            <span className={`text-sm font-bold ${
                              rate >= 20 ? 'text-red-600' : rate >= 10 ? 'text-orange-500' : 'text-yellow-600'
                            }`}>%{rate.toFixed(1)}</span>
                          </td>
                          <td className="py-2.5 pl-3">
                            <div className="w-full bg-gray-100 rounded-full h-2">
                              <div className="h-2 rounded-full"
                                style={{ width: `${Math.min(rate, 100)}%`, backgroundColor: barColor }} />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
